import { useEffect, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { TextArea } from "@/components/ui/TextArea";
import {
  ancestryOptions,
  attributeLabels,
  classOptions,
  createDefaultCharacterInput,
  originOptions,
} from "@/features/characters/defaults";
import type { CharacterInput } from "@/features/characters/types";

interface CharacterFormProps {
  initialValue?: CharacterInput;
  submitLabel: string;
  onSubmit: (value: CharacterInput) => Promise<void>;
}

export function CharacterForm({
  initialValue,
  submitLabel,
  onSubmit,
}: CharacterFormProps) {
  const [form, setForm] = useState<CharacterInput>(
    initialValue ?? createDefaultCharacterInput(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialValue) {
      setForm(initialValue);
    }
  }, [initialValue]);

  function updateField<Key extends keyof CharacterInput>(
    key: Key,
    value: CharacterInput[Key],
  ) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function updateAttribute(attribute: keyof CharacterInput["attributes"], value: number) {
    setForm((current) => ({
      ...current,
      attributes: {
        ...current.attributes,
        [attribute]: value,
      },
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Informe ao menos o nome do personagem.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        ...form,
        level: Math.max(1, Number(form.level) || 1),
        name: form.name.trim(),
        epithet: form.epithet.trim(),
        summary: form.summary.trim(),
        goals: form.goals.trim(),
        equipment: form.equipment.trim(),
        notes: form.notes.trim(),
      });
    } catch {
      setError("Não foi possível salvar a ficha agora.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="grid gap-6" onSubmit={handleSubmit}>
      <Card
        title="Identidade heroica"
        subtitle="Campos base para nome, papel e contexto do personagem."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Nome"
            name="name"
            placeholder="Ex.: Kael Drakar"
            value={form.name}
            onChange={(event) => updateField("name", event.target.value)}
          />
          <Input
            label="Epíteto"
            name="epithet"
            placeholder="Ex.: o Vigia Rubro"
            value={form.epithet}
            onChange={(event) => updateField("epithet", event.target.value)}
          />
          <Select
            label="Raça"
            name="ancestry"
            options={ancestryOptions}
            value={form.ancestry}
            onChange={(event) => updateField("ancestry", event.target.value)}
          />
          <Select
            label="Classe"
            name="className"
            options={classOptions}
            value={form.className}
            onChange={(event) => updateField("className", event.target.value)}
          />
          <Select
            label="Origem"
            name="origin"
            options={originOptions}
            value={form.origin}
            onChange={(event) => updateField("origin", event.target.value)}
          />
          <Input
            label="Nível"
            name="level"
            type="number"
            min={1}
            max={20}
            value={form.level}
            onChange={(event) => updateField("level", Number(event.target.value))}
          />
        </div>
      </Card>

      <Card
        title="Atributos"
        subtitle="Base numérica da ficha, pronta para regras futuras."
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {attributeLabels.map((attribute) => (
            <Input
              key={attribute}
              label={attribute}
              type="number"
              min={1}
              max={30}
              value={form.attributes[attribute]}
              onChange={(event) =>
                updateAttribute(attribute, Number(event.target.value) || 1)
              }
            />
          ))}
        </div>
      </Card>

      <Card
        title="Narrativa e anotações"
        subtitle="Espaço para lore, metas, equipamentos e observações."
      >
        <div className="grid gap-4">
          <TextArea
            label="Resumo"
            name="summary"
            placeholder="Quem é esse personagem, como ele age e o que o torna marcante?"
            value={form.summary}
            onChange={(event) => updateField("summary", event.target.value)}
          />
          <div className="grid gap-4 lg:grid-cols-2">
            <TextArea
              label="Objetivos"
              name="goals"
              placeholder="Quais juramentos, ambições ou missões ele carrega?"
              value={form.goals}
              onChange={(event) => updateField("goals", event.target.value)}
            />
            <TextArea
              label="Equipamentos"
              name="equipment"
              placeholder="Armas, itens mágicos, relíquias e recursos importantes."
              value={form.equipment}
              onChange={(event) => updateField("equipment", event.target.value)}
            />
          </div>
          <TextArea
            label="Notas livres"
            name="notes"
            placeholder="Detalhes de campanha, vínculos, segredos ou lembretes."
            value={form.notes}
            onChange={(event) => updateField("notes", event.target.value)}
          />
        </div>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {error ? <p className="text-sm text-red-200">{error}</p> : <span />}
        <Button type="submit" disabled={submitting}>
          {submitting ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
